import { GraphQLUserRepository } from "@app/repositories/GraphQLRepositories/UserRepository";
import { UserRole, UserType } from "@generated/client/graphql";
import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";
import { userKeys } from "./queryKeys";
import { skipToken, useQuery } from "@tanstack/react-query";

const userRepository = new GraphQLUserRepository();

// read
export const usersAtom = atomWithQuery(() => {
    return {
        queryKey: [userKeys.users],
        queryFn: userRepository.get,
    };
});

export const currentUserAtom = atomWithQuery(() => {
    return {
        queryKey: [userKeys.currentUser],
        queryFn: userRepository.getCurrentUser,
        staleTime: Infinity,
    };
});

export const currentUserNoTenantAtom = atomWithQuery(() => {
    return {
        queryKey: [userKeys.currentUserNoTenant],
        queryFn: userRepository.getCurrentUserNoTenant,
        staleTime: Infinity,
    };
});

export const currentUserLocaleAtom = atom((get) => {
    const currentUserQuery = get(currentUserAtom);
    const locale: "en" | "fr" | undefined | null =
        currentUserQuery?.data?.currentUser.locale;
    return locale;
});

// read-only
export const currentUserRoleAtom = atom((get) => {
    const currentUserQuery = get(currentUserAtom);
    const role = currentUserQuery?.data?.currentUser.permission?.role;
    if (!role && !currentUserQuery?.isPending) {
        throw new Error("Role not found");
    }

    const isBeavrAdmin = role === UserRole.BeavrAdmin;
    const isAdmin = role === UserRole.Admin;
    const isContributor = role === UserRole.Contributor;
    const isSupplier = role === UserRole.Supplier;

    return {
        role,
        isPending: currentUserQuery.isPending,
        isBeavrAdmin,
        isAdmin,
        isContributor,
        isSupplier,
    };
});

export const currentUserIsBeavrAdminAtom = atom((get) => {
    const { role } = get(currentUserRoleAtom);

    return role === UserRole.BeavrAdmin;
});
// read-only
export const currentUserIsBeavrAtom = atom((get) => {
    const currentUserNoTenantQuery = get(currentUserNoTenantAtom);
    const type = currentUserNoTenantQuery?.data?.currentUserNoTenant?.type;
    const isBeavrUser = [UserType.Beavr, UserType.BeavrSuperAdmin].includes(
        type || UserType.Customer,
    );
    return { isBeavrUser, isPending: currentUserNoTenantQuery.isPending };
});

export const currentUserOrgsAtom = atomWithQuery(() => {
    return {
        queryKey: [userKeys.currentUserOrgsAtom],
        queryFn: userRepository.getCurrentUserOrgs,
        staleTime: Infinity,
    };
});

export const useUser = (id?: string) => {
    const { data } = useQuery({
        queryKey: ["user", id],
        queryFn: id ? () => userRepository.getUser(id) : skipToken,
    });
    return data?.user;
};
